import { useCallback, useEffect, useRef } from 'react'
import {
  trackUserNoticeActionTapped,
  trackUserNoticeDismissed,
  trackUserNoticeScreenView,
  type UserNoticeSource,
  type UserNoticeVariant,
} from '../analytics'

interface UserNoticeTrackingOptions {
  source: UserNoticeSource
  noticeType: UserNoticeVariant
  actionUrl: string
  onDismiss: () => void
}

// What both variants do around the payment form, in one place: report the view, hand off
// to the form, and report a close only when the user left WITHOUT handing off.
export function useUserNoticeTracking({
  source,
  noticeType,
  actionUrl,
  onDismiss,
}: UserNoticeTrackingOptions) {
  // A ref, not state: nothing renders from it, and the handlers must read the value the
  // click just wrote rather than the one their closure captured.
  const handedOff = useRef(false)

  useEffect(() => {
    trackUserNoticeScreenView(source, noticeType)
  }, [source, noticeType])

  const onClose = useCallback(() => {
    if (!handedOff.current) trackUserNoticeDismissed(source, noticeType)
    onDismiss()
  }, [source, noticeType, onDismiss])

  const onUpdate = useCallback(() => {
    handedOff.current = true
    trackUserNoticeActionTapped(source, noticeType)
    window.location.assign(actionUrl)
  }, [source, noticeType, actionUrl])

  return { onClose, onUpdate }
}
